import { useState, useEffect, useMemo } from 'react'
import { api } from '../../services/api'
import ConfirmModal from '../ui/ConfirmModal'

interface Client {
  id: string
  name: string
  slug: string
  linkedTenantId: string | null
  linkedTenant: {
    id: string
    name: string
    slug: string
    plan?: string | null
    lastBillableUsers?: number | null
    lastMonthlyTotal?: number | null
    lastPeriod?: string | null
  } | null
  status: 'active' | 'inactive' | 'prospect'
  clientType: 'professional' | 'hybrid'
  ownerUserId: string | null
  owner: { id: string; name: string | null; email: string | null } | null
  deletedAt: string | null
  contactName: string | null
  contactEmail: string | null
  contactPhone: string | null
  legalName: string | null
  taxId: string | null
  billingEmails: string[]
  address: string | null
  paymentTerms: 'net_15' | 'net_30' | 'net_45' | 'net_60'
  currency: string
  notes: string | null
  createdAt: string
  updatedAt: string
  activeServicesCount?: number
}

interface ClientListProps {
  onEdit?: (client: Client) => void
  onDelete?: (clientId: string) => void
  onManageServices?: (client: Client) => void
  isReadOnly?: boolean
  refreshTrigger?: number
}

export default function ClientList({ onEdit, onDelete, onManageServices, isReadOnly = false, refreshTrigger = 0 }: ClientListProps) {
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [ownerFilter, setOwnerFilter] = useState<string>('')
  const [searchText, setSearchText] = useState<string>('')
  const [showDeleted, setShowDeleted] = useState<boolean>(false)
  const [teamUsers, setTeamUsers] = useState<Array<{ id: string; name: string | null; email: string }>>([])

  // Delete flow — two-stage with confirm modal (instead of native confirm()).
  const [deleteTarget, setDeleteTarget] = useState<Client | null>(null)
  const [deleteHistoricalCount, setDeleteHistoricalCount] = useState<number | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Debounce search — keep list responsive as user types.
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(searchText.trim()), 250)
    return () => clearTimeout(h)
  }, [searchText])

  useEffect(() => {
    fetchClients()
  }, [refreshTrigger, statusFilter, typeFilter, ownerFilter, debouncedSearch, showDeleted])

  useEffect(() => {
    // Load team users once for the owner filter.
    api.listTeamUsers().then(res => {
      if (res.success && res.data) {
        setTeamUsers((res.data as { users: typeof teamUsers }).users || [])
      }
    }).catch(() => { /* non-fatal */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchClients = async () => {
    try {
      setLoading(true)
      setError(null)
      const params: { status?: string; type?: string; q?: string; owner?: string; includeDeleted?: boolean } = {}
      if (statusFilter) params.status = statusFilter
      if (typeFilter) params.type = typeFilter
      if (ownerFilter) params.owner = ownerFilter
      if (debouncedSearch) params.q = debouncedSearch
      if (showDeleted) params.includeDeleted = true

      const response = await api.listClients(params)
      if (response.success && response.data) {
        setClients((response.data as { clients: Client[] }).clients || [])
      } else {
        setError(response.error || 'Error al cargar clientes')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  // Kick off a soft-delete. May take two steps if the client has
  // historical services — we surface the count in the modal body.
  const startDelete = (client: Client) => {
    setDeleteTarget(client)
    setDeleteHistoricalCount(null)
    setDeleteError(null)
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    setDeleteBusy(true)
    setDeleteError(null)
    try {
      const needsConfirm = deleteHistoricalCount !== null && deleteHistoricalCount > 0
      const response = await api.deleteClient(deleteTarget.id, needsConfirm ? { confirm: true } : undefined)
      if (!response.success) {
        const err = response as unknown as { requiresConfirmation?: boolean; historicalCount?: number; error?: string }
        if (err.requiresConfirmation) {
          // Stage 2 — surface the count and let the user re-confirm.
          setDeleteHistoricalCount(err.historicalCount || 0)
          setDeleteBusy(false)
          return
        }
        setDeleteError(err.error || 'Error al eliminar cliente')
        setDeleteBusy(false)
        return
      }
      onDelete?.(deleteTarget.id)
      setDeleteTarget(null)
      setDeleteHistoricalCount(null)
      fetchClients()
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Error al eliminar cliente')
    } finally {
      setDeleteBusy(false)
    }
  }

  const handleRestore = async (client: Client) => {
    try {
      await api.restoreClient(client.id)
      fetchClients()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al restaurar cliente')
    }
  }

  // Derived values for the delete modal body so the text stays in
  // sync with the two-stage flow without re-rendering logic.
  const deleteModal = useMemo(() => {
    if (!deleteTarget) return null
    if (deleteError) {
      return {
        title: 'Error',
        message: deleteError,
        detail: null,
        variant: 'warning' as const,
        confirmLabel: 'Cerrar',
        onConfirm: () => { setDeleteTarget(null); setDeleteError(null) },
      }
    }
    if (deleteHistoricalCount !== null && deleteHistoricalCount > 0) {
      return {
        title: `¿Enviar ${deleteTarget.name} a la papelera?`,
        message: 'Se enviarán también los servicios históricos que están asociados.',
        detail: `${deleteHistoricalCount} servicio(s) en historial se moverán junto con el cliente. Podés restaurarlo desde "Ver papelera".`,
        variant: 'danger' as const,
        confirmLabel: 'Enviar a papelera',
        onConfirm: handleConfirmDelete,
      }
    }
    return {
      title: `¿Enviar ${deleteTarget.name} a la papelera?`,
      message: 'El cliente dejará de aparecer en la lista pero podrás restaurarlo más tarde.',
      detail: null,
      variant: 'danger' as const,
      confirmLabel: 'Enviar a papelera',
      onConfirm: handleConfirmDelete,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteTarget, deleteHistoricalCount, deleteError])

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-xl p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-600 rounded w-1/4" />
          <div className="h-12 bg-gray-600 rounded" />
          <div className="h-12 bg-gray-600 rounded" />
          <div className="h-12 bg-gray-600 rounded" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-gray-800 rounded-xl p-8 text-center">
        <svg className="w-12 h-12 mx-auto text-severity-critical mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <p className="text-gray-300">{error}</p>
        <button
          onClick={fetchClients}
          className="mt-4 px-4 py-2 bg-accent hover:bg-accent-hover text-white rounded-lg transition-colors"
        >
          Reintentar
        </button>
      </div>
    )
  }

  const getStatusBadge = (status: Client['status']) => {
    const styles = {
      active: 'bg-severity-success/20 text-severity-success',
      inactive: 'bg-gray-600 text-gray-300',
      prospect: 'bg-severity-warning/20 text-severity-warning'
    }
    const labels = {
      active: 'Activo',
      inactive: 'Inactivo',
      prospect: 'Prospecto'
    }
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    )
  }

  const getTypeBadge = (type: Client['clientType'], linkedTenant: Client['linkedTenant']) => {
    if (type === 'hybrid' && linkedTenant) {
      return (
        <div className="flex items-center gap-1">
          <span className="bg-purple-500/20 text-purple-400 px-2 py-1 rounded-full text-xs font-medium">
            Hibrido
          </span>
          <span className="text-xs text-gray-500">
            ({linkedTenant.name})
          </span>
        </div>
      )
    }
    return (
      <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full text-xs font-medium">
        Profesional
      </span>
    )
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        {/* Search input with icon */}
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder="Buscar por nombre, slug, razón social o RUT…"
            className="w-full pl-9 pr-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-accent focus:border-transparent"
          />
          {searchText && (
            <button
              onClick={() => setSearchText('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
              title="Limpiar búsqueda"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-accent focus:border-transparent"
        >
          <option value="">Todos los estados</option>
          <option value="active">Activo</option>
          <option value="inactive">Inactivo</option>
          <option value="prospect">Prospecto</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-accent focus:border-transparent"
        >
          <option value="">Todos los tipos</option>
          <option value="professional">Solo Profesional</option>
          <option value="hybrid">Hibrido (con Tenant)</option>
        </select>
        <select
          value={ownerFilter}
          onChange={e => setOwnerFilter(e.target.value)}
          className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-accent focus:border-transparent"
          title="Filtrar por owner"
        >
          <option value="">Todos los owners</option>
          <option value="none">Sin owner asignado</option>
          {teamUsers.map(u => (
            <option key={u.id} value={u.id}>{u.name || u.email}</option>
          ))}
        </select>

        <button
          onClick={() => setShowDeleted(v => !v)}
          className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
            showDeleted
              ? 'bg-red-500/20 border-red-500/40 text-red-300'
              : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'
          }`}
          title={showDeleted ? 'Ver clientes activos' : 'Ver clientes en papelera'}
        >
          {showDeleted ? '← Volver a activos' : '🗑 Ver papelera'}
        </button>

        <span className="text-sm text-gray-400 ml-auto">
          {clients.length} cliente(s)
        </span>
      </div>

      {/* Table */}
      {clients.length === 0 ? (
        <div className="bg-gray-800 rounded-xl p-12 text-center border border-gray-700">
          <svg
            className="w-16 h-16 text-gray-500 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <h3 className="text-xl font-semibold text-white mb-2">
            No hay clientes de servicios profesionales
          </h3>
          <p className="text-gray-300">
            Agrega tu primer cliente para comenzar a gestionar servicios profesionales.
          </p>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Servicios
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Contacto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Owner
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {clients.map((client) => (
                <tr key={client.id} className="hover:bg-gray-700/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold mr-3"
                        style={{ backgroundColor: client.clientType === 'hybrid' ? '#8B5CF6' : '#FF9B71' }}
                      >
                        {client.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-white font-medium">{client.name}</div>
                        <div className="text-gray-500 text-sm">{client.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(client.status)}
                  </td>
                  <td className="px-6 py-4">
                    {getTypeBadge(client.clientType, client.linkedTenant)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <div>
                        <span className="text-white font-medium">
                          {client.activeServicesCount || 0}
                        </span>
                        <span className="text-gray-500 text-sm ml-1">
                          activo(s)
                        </span>
                      </div>
                      {/* For hybrid clients, surface the tenant's recurring
                          billing so Finanzas sees at a glance that this
                          client already has auto-updating monthly invoice
                          from the day-15 snapshot. */}
                      {client.clientType === 'hybrid' && client.linkedTenant && (
                        client.linkedTenant.lastMonthlyTotal !== null && client.linkedTenant.lastMonthlyTotal !== undefined ? (
                          <div className="text-xs text-green-400 flex items-center gap-1" title="Facturación recurrente automática del tenant (se actualiza cada día 15 con los usuarios de JumpCloud)">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            {client.linkedTenant.lastBillableUsers} lic · ${client.linkedTenant.lastMonthlyTotal.toLocaleString()}/mes
                          </div>
                        ) : (
                          <div className="text-xs text-yellow-400/80" title="El tenant está vinculado pero todavía no tiene snapshot de facturación (próximo día 15)">
                            Recurrente pendiente
                          </div>
                        )
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {client.contactEmail ? (
                      <div>
                        <div className="text-white text-sm">{client.contactName || '-'}</div>
                        <div className="text-gray-500 text-xs">{client.contactEmail}</div>
                      </div>
                    ) : (
                      <span className="text-gray-500 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {client.owner ? (
                      <div>
                        <div className="text-white text-sm">{client.owner.name || client.owner.email?.split('@')[0]}</div>
                        <div className="text-gray-500 text-xs">{client.owner.email}</div>
                      </div>
                    ) : (
                      <span className="text-yellow-400/70 text-xs">Sin asignar</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {/* Manage Services */}
                      {onManageServices && (
                        <button
                          onClick={() => onManageServices(client)}
                          className="text-gray-300 hover:text-accent p-2 rounded-lg hover:bg-gray-600 transition-colors"
                          title="Gestionar servicios"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                          </svg>
                        </button>
                      )}

                      {/* Edit */}
                      {!isReadOnly && onEdit && (
                        <button
                          onClick={() => onEdit(client)}
                          className="text-gray-300 hover:text-accent p-2 rounded-lg hover:bg-gray-600 transition-colors"
                          title="Editar cliente"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      )}

                      {/* Restore (papelera mode) or Delete */}
                      {!isReadOnly && client.deletedAt ? (
                        <button
                          onClick={() => handleRestore(client)}
                          className="text-green-400 hover:text-green-300 p-2 rounded-lg hover:bg-green-500/10 transition-colors"
                          title="Restaurar cliente"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>
                      ) : !isReadOnly && (
                        <button
                          onClick={() => startDelete(client)}
                          className="text-gray-300 hover:text-red-400 p-2 rounded-lg hover:bg-gray-600 transition-colors"
                          title="Enviar a papelera"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Soft-delete confirm modal (replaces native confirm()).
          Uses the two-stage flow: first shows a simple confirm, and if
          the backend reports historical rows, re-asks with the count. */}
      {deleteTarget && deleteModal && (
        <ConfirmModal
          open={true}
          title={deleteModal.title}
          message={deleteModal.message}
          detail={deleteModal.detail}
          variant={deleteModal.variant}
          confirmLabel={deleteModal.confirmLabel}
          busy={deleteBusy}
          onConfirm={deleteModal.onConfirm}
          onCancel={() => { if (!deleteBusy) { setDeleteTarget(null); setDeleteHistoricalCount(null); setDeleteError(null) } }}
        />
      )}
    </div>
  )
}
